import type { DayType, MealSlot, TrainingTimeOfDay } from '@cadencia/contracts';
import { SAFETY_LIMITS } from '../safety/limits.js';
import { roundTo } from '../units/round.js';
import {
  normalizeMealSlots,
  workoutMealRelations,
  type WorkoutMealRelation,
} from './meal-schedule.js';
import { macrosToKcal, type MacroTargets } from './targets.js';

/**
 * Reparto de los macros del dia entre las comidas ELEGIDAS
 * (docs/NUTRITION_ENGINE.md §4.3).
 *
 * QUE HACE Y QUE NO
 *
 * Recibe los macros del dia —que ya vienen del §4.1 y del §4.2, con su tipo de
 * dia y su modulacion de hidratos— y decide como se reparten entre las comidas
 * que el usuario hace. **No toca los totales del dia.** Entrenar por la mañana
 * o por la tarde no cambia cuantos hidratos se comen: cambia CUANDO. El
 * invariante esta escrito como test y no como comentario.
 *
 * ESTA ES LA UNICA HEURISTICA PERI-ENTRENAMIENTO DEL SISTEMA (§18 del encargo).
 * Los porcentajes viven en `PERI_WORKOUT` y en ningun sitio mas.
 */

/**
 * Peso energetico de cada hueco antes de normalizar.
 *
 * Se normaliza siempre sobre las comidas ELEGIDAS, asi que estos numeros solo
 * dicen proporciones entre si: con desayuno, almuerzo y cena, el 0,25 / 0,35 /
 * 0,25 se convierte en 29 % / 41 % / 29 %. Quien haga una sola comida al dia
 * se la lleva entera, que es lo unico que puede pasar.
 *
 * La merienda pesa como la media mañana: son la misma clase de ingesta.
 */
export const SLOT_BASE_WEIGHT: Record<MealSlot, number> = {
  breakfast: 0.25,
  midmorning: 0.15,
  lunch: 0.35,
  afternoon_snack: 0.15,
  extra: 0.12,
  dinner: 0.25,
};

/**
 * La heuristica peri-entrenamiento, entera y en un solo sitio.
 *
 * EL 30 % SIGUE SIENDO EL PUNTO DE PARTIDA, Y YA NO ES UNA REGLA RIGIDA.
 *
 * Con cuatro o cinco comidas, fijar el 30 % de los hidratos en la de antes y
 * otro 30 % en la de despues deja un 40 % para las demas y todo cuadra. Con dos
 * comidas al dia y las dos pegadas al entrenamiento, el 30 + 30 dejaba un 40 %
 * sin ningun hueco donde ponerlo: los hidratos del dia sumaban el 60 % del
 * objetivo y nadie lo decia. Era inalcanzable mientras los huecos los elegia el
 * motor —siempre cuatro o cinco— y dejo de serlo el 08/09/2026.
 *
 * Por eso las cuotas son NOMINALES y se reescalan cuando no caben
 * (`repartirConCuotas`). El 30 % se respeta siempre que haya comidas libres que
 * absorban el resto, y cuando no las hay se reparte proporcionalmente entre las
 * que estan alrededor del entrenamiento, que es lo unico coherente: si las dos
 * unicas comidas del dia son la de antes y la de despues, todos los hidratos
 * del dia son peri-entrenamiento por definicion.
 */
export const PERI_WORKOUT = {
  /** Fraccion nominal de los hidratos del dia en la comida previa y en la posterior. */
  carbShare: 0.3,
  /**
   * Cuota de la comida `near_workout`: la unica que hay al lado del
   * entrenamiento cuando falta la del otro lado. Se lleva algo menos que la
   * suma de las dos —no es dos comidas— pero bastante mas que una normal.
   */
  carbShareNear: 0.45,
  /**
   * Tope de lo que puede acumularse alrededor del entrenamiento cuando SI hay
   * comidas libres. Evita que el resto del dia se quede sin hidratos por
   * cumplir una tabla.
   */
  maxCombinedCarbShare: 0.7,
  /**
   * Cuanto puede concentrar una comida por encima de su peso propio.
   *
   * ES LA PIEZA QUE IMPIDE LA SITUACION ABSURDA, y salio de un dia que dejo de
   * cerrar. Entrenamiento de manana con desayuno, media mañana, almuerzo y
   * cena: la comida posterior es la media mañana, que pesa el 15 % del dia, y
   * el 30 % nominal le pedia 117 g de hidratos —468 kcal solo de hidratos— a
   * un hueco cuyo tamaño es de 415. Ninguna receta de media mañana da eso, asi
   * que el generador elegia platos imposibles y el dia entero se quedaba en
   * `partial` con los hidratos un 11 % por debajo.
   *
   * Con 1,8, esa media mañana se lleva el 27 % en vez del 30 y el resto vuelve
   * al reparto. El 30 % sigue vigente donde cabe —una comida principal pesa
   * bastante mas que 0,3/1,8— y deja de aplicarse donde no.
   */
  maxCarbConcentration: 1.8,
  /** La comida previa lleva grasa baja: retrasa el vaciado gastrico. */
  fatFactorPreWorkout: 0.5,
  /** Proteina minima en la comida posterior, en gramos. */
  minPostWorkoutProteinG: 30,
} as const;

/**
 * Proteina minima por toma, en g/kg.
 *
 * YA NO SON CUATRO TOMAS (§7.1 del encargo). El §4.3 exigia «proteina repartida
 * en 4 tomas de >=0,3 g/kg», y eso obligaba a inventar comidas que el usuario no
 * hace: con tres comidas elegidas, la cuarta toma solo podia salir de un hueco
 * `extra` que nadie ha pedido.
 *
 * La regla nueva: el objetivo DIARIO de proteina manda, se reparte entre las
 * comidas elegidas y cada una aspira a este minimo. Con cinco comidas y el
 * suelo diario de 1,6 g/kg, 5 x 0,3 = 1,5 <= 1,6: el minimo por toma siempre
 * cabe. Con menos comidas la toma es necesariamente mayor y no se limita por
 * arriba, porque no hay ninguna razon fisiologica para hacerlo y si la hay para
 * llegar al total.
 */
export const MIN_PROTEIN_PER_MEAL_G_PER_KG = 0.3;

function normalizar(pesos: ReadonlyMap<MealSlot, number>): Map<MealSlot, number> {
  const total = [...pesos.values()].reduce((a, b) => a + b, 0);
  if (total <= 0) throw new Error('Reparto sin peso');
  return new Map([...pesos].map(([slot, peso]) => [slot, peso / total]));
}

/**
 * Fija la cuota de algunos huecos y reparte el resto entre los demas.
 *
 * Tres casos, y el tercero es el que faltaba:
 *
 *   a) Hay huecos libres y las cuotas caben: cada fijo se lleva lo suyo y los
 *      libres se reparten el resto en proporcion a su peso base.
 *   b) Hay huecos libres pero las cuotas se pasan del tope: las cuotas se
 *      escalan a `maxCombinedCarbShare` para que a los libres les quede algo.
 *   c) NO hay huecos libres: las cuotas se escalan para sumar 1. Sin esto, el
 *      reparto devolvia menos del 100 % de los hidratos del dia y el objetivo
 *      por comida salia bajo sin que nada fallara.
 */
function repartirConCuotas(
  base: ReadonlyMap<MealSlot, number>,
  cuotas: ReadonlyMap<MealSlot, number>,
): Map<MealSlot, number> {
  const libres = [...base].filter(([slot]) => !cuotas.has(slot));
  const sumaCuotas = [...cuotas.values()].reduce((a, b) => a + b, 0);
  if (sumaCuotas <= 0) return new Map(base);

  if (libres.length === 0) {
    // Caso (c): todo el dia esta alrededor del entrenamiento.
    return new Map([...cuotas].map(([slot, cuota]) => [slot, cuota / sumaCuotas]));
  }

  // Caso (b): se recortan las cuotas para dejar sitio a las comidas libres.
  const factor = sumaCuotas > PERI_WORKOUT.maxCombinedCarbShare
    ? PERI_WORKOUT.maxCombinedCarbShare / sumaCuotas
    : 1;

  const salida = new Map<MealSlot, number>();
  let fijado = 0;
  for (const [slot, cuota] of cuotas) {
    const valor = cuota * factor;
    salida.set(slot, valor);
    fijado += valor;
  }

  const resto = Math.max(0, 1 - fijado);
  const pesoLibre = libres.reduce((total, [, peso]) => total + peso, 0);
  for (const [slot, peso] of libres) {
    salida.set(slot, pesoLibre > 0 ? (peso / pesoLibre) * resto : resto / libres.length);
  }
  return salida;
}

export interface SlotSplitInput {
  day: MacroTargets;
  /** Las comidas elegidas por el usuario. Se normaliza el orden. */
  slots: readonly MealSlot[];
  bodyWeightKg: number;
  dayType: DayType;
  /** Turno ya resuelto. `null` = sin sesion de fuerza o sin saber cuando. */
  timing?: TrainingTimeOfDay | null;
}

export interface SlotSplit {
  slot: MealSlot;
  relation: WorkoutMealRelation;
  target: MacroTargets;
  /** Proteina minima exigible a este hueco. */
  minProteinG: number;
}

/**
 * Reparte los macros del dia entre las comidas elegidas.
 *
 * Orden de las decisiones, que importa:
 *
 *   1. Peso energetico base de cada comida, normalizado a las elegidas.
 *   2. Relacion de cada comida con el entrenamiento (`workoutMealRelations`).
 *   3. Hidratos: cuotas peri-entrenamiento nominales, reescaladas si no caben.
 *   4. Grasa: se baja en las comidas pegadas al entrenamiento y se renormaliza.
 *   5. Proteina: reparto uniforme, y despues se le sube el suelo a la comida
 *      posterior quitandoselo a las demas en proporcion. La SUMA no cambia.
 */
export function splitDayIntoSlots(input: SlotSplitInput): SlotSplit[] {
  const slots = normalizeMealSlots(input.slots);
  if (slots.length === 0) throw new Error('Un dia sin comidas no se puede repartir');

  const base = normalizar(new Map(slots.map((slot) => [slot, SLOT_BASE_WEIGHT[slot]])));

  const relaciones = workoutMealRelations({
    slots,
    dayType: input.dayType,
    timing: input.timing ?? null,
  });

  const cuotasCarbo = new Map<MealSlot, number>();
  for (const [slot, relacion] of relaciones) {
    const nominal =
      relacion === 'pre_workout' || relacion === 'post_workout'
        ? PERI_WORKOUT.carbShare
        : relacion === 'near_workout'
          ? PERI_WORKOUT.carbShareNear
          : null;
    if (nominal === null) continue;
    // Nadie se lleva mas hidratos de los que le caben. Ver `maxCarbConcentration`.
    const techo = (base.get(slot) ?? 0) * PERI_WORKOUT.maxCarbConcentration;
    cuotasCarbo.set(slot, Math.min(nominal, techo));
  }
  const carbo = cuotasCarbo.size > 0 ? repartirConCuotas(base, cuotasCarbo) : base;

  const pegadaAlEntreno = (slot: MealSlot): boolean => {
    const relacion = relaciones.get(slot);
    return relacion === 'pre_workout' || relacion === 'post_workout' || relacion === 'near_workout';
  };

  const grasaBruta = new Map<MealSlot, number>(
    [...base].map(([slot, peso]) => [
      slot,
      pegadaAlEntreno(slot) ? peso * PERI_WORKOUT.fatFactorPreWorkout : peso,
    ]),
  );
  const grasa = normalizar(grasaBruta);

  const proteina = repartoDeProteina(slots, relaciones, input.day.proteinG, input.bodyWeightKg);

  return slots.map((slot) => {
    const proteinG = roundTo(proteina.get(slot) ?? 0, 1);
    const carbsG = roundTo(input.day.carbsG * (carbo.get(slot) ?? 0), 1);
    const fatG = roundTo(input.day.fatG * (grasa.get(slot) ?? 0), 1);
    return {
      slot,
      relation: relaciones.get(slot) ?? 'regular',
      target: {
        // LAS KCAL DEL HUECO SON LAS DE SUS PROPIOS MACROS, no una fraccion
        // aparte del dia. Con dos cuentas distintas el objetivo de un hueco
        // podia contradecirse consigo mismo —415 kcal y 117 g de hidratos, que
        // ya son 468— y el generador buscaba recetas que no existen. La suma de
        // los huecos sigue siendo el dia porque las tres proporciones suman 1.
        kcal: macrosToKcal({ proteinG, carbsG, fatG }),
        proteinG,
        carbsG,
        fatG,
      },
      minProteinG: minProteinDe(slot, relaciones, input.bodyWeightKg),
    };
  });
}

/**
 * Gramos de proteina de cada comida.
 *
 * Punto de partida uniforme —`dailyProtein / n`, como dice el §19 del encargo—
 * y despues UN ajuste: la comida posterior al entrenamiento no puede quedarse
 * por debajo de `minPostWorkoutProteinG`, y lo que se le añade sale de las
 * demas en proporcion a lo que tienen. Con esto la SUMA sigue siendo el
 * objetivo del dia, que es la invariante que importa.
 *
 * No se hace nada mas sofisticado a proposito: el reparto fino lo termina el
 * generador, que puede mover proteina entre comidas dentro de las tolerancias
 * segun lo que de de si cada receta. Un reparto rigido aqui solo conseguiria
 * que ninguna comida cerrara.
 */
function repartoDeProteina(
  slots: readonly MealSlot[],
  relaciones: ReadonlyMap<MealSlot, WorkoutMealRelation>,
  dailyProteinG: number,
  bodyWeightKg: number,
): Map<MealSlot, number> {
  const porHueco = dailyProteinG / slots.length;
  const salida = new Map<MealSlot, number>(slots.map((slot) => [slot, porHueco]));

  const post = slots.find((slot) => relaciones.get(slot) === 'post_workout');
  if (post === undefined) return salida;

  const minimo = Math.min(
    dailyProteinG,
    Math.max(PERI_WORKOUT.minPostWorkoutProteinG, MIN_PROTEIN_PER_MEAL_G_PER_KG * bodyWeightKg),
  );
  const falta = minimo - porHueco;
  if (falta <= 0) return salida;

  const otros = slots.filter((slot) => slot !== post);
  const disponible = otros.reduce((total, slot) => total + (salida.get(slot) ?? 0), 0);
  if (disponible <= 0) return salida;

  const cedido = Math.min(falta, disponible);
  for (const slot of otros) {
    const actual = salida.get(slot) ?? 0;
    salida.set(slot, actual - cedido * (actual / disponible));
  }
  salida.set(post, porHueco + cedido);
  return salida;
}

/**
 * Proteina minima que se le exige a una comida.
 *
 * `0,3 g/kg` en cada una; en la posterior al entrenamiento, ademas los 30 g del
 * §4.3. Con cinco comidas eso pide 1,5 g/kg al dia y el suelo diario es 1,6, asi
 * que siempre cabe; con menos comidas, sobra de largo.
 *
 * Es un objetivo por toma, no un limite duro: el limite duro es el suelo
 * DIARIO, y ese lo protegen `macroTargets` al calcularlo y `adjustDay` al
 * comprobar el resultado.
 */
function minProteinDe(
  slot: MealSlot,
  relaciones: ReadonlyMap<MealSlot, WorkoutMealRelation>,
  bodyWeightKg: number,
): number {
  const porToma = MIN_PROTEIN_PER_MEAL_G_PER_KG * bodyWeightKg;
  if (relaciones.get(slot) === 'post_workout') {
    return roundTo(Math.max(PERI_WORKOUT.minPostWorkoutProteinG, porToma), 1);
  }
  return roundTo(porToma, 1);
}

/**
 * Ninguna comida puede pasar de este porcentaje de las kcal del dia (§7.1).
 *
 * OJO: es un limite del REPARTO entre varias comidas, no un limite clinico. Con
 * una sola comida al dia es inalcanzable por definicion —esa comida es el 100 %
 * del dia— y quien lo aplique tiene que saltarselo en ese caso en vez de
 * bloquear el ajuste. Ver `day-adjust.ts`.
 */
export const MAX_MEAL_SHARE_OF_DAY = 0.45;

/** Comprueba las reglas del §4.3 sobre un reparto ya calculado. */
export function checkSplit(
  splits: readonly SlotSplit[],
  dayKcal: number,
): { ok: boolean; reasonCodes: string[] } {
  const reasonCodes: string[] = [];
  // Con una sola comida el tope del 45 % no se puede cumplir y no significa
  // nada: no hay reparto que hacer.
  if (splits.length > 1) {
    for (const split of splits) {
      if (split.target.kcal > dayKcal * MAX_MEAL_SHARE_OF_DAY) {
        reasonCodes.push(`meal_over_share_${split.slot}`);
      }
    }
  }
  const proteinaTotal = splits.reduce((total, split) => total + split.target.proteinG, 0);
  if (proteinaTotal <= 0) reasonCodes.push('split_without_protein');
  return { ok: reasonCodes.length === 0, reasonCodes };
}

/** Suelo de proteina del dia, en gramos (limite duro de `safety/limits.ts`). */
export function dayProteinFloorG(bodyWeightKg: number): number {
  return roundTo(SAFETY_LIMITS.macros.minProteinGPerKg * bodyWeightKg, 1);
}
